import { useApolloClient } from '@apollo/react-hooks';
import moment from 'moment-timezone';
import CURRENT_USER from '../GraphQl/Queries/CURRENT_USER';

/**
 * React hook for timezone management
 * Returns the effective timezone and conversion utilities
 */
export const useTimezone = () => {
  const client = useApolloClient();

  /**
   * Precedence: User Timezone > Org Timezone > UTC
   * @returns {string} - IANA timezone identifier (e.g., 'America/New_York', 'UTC')
   */
  const getTimezoneFromCache = () => {
    try {
      const { currentUser } = client.readQuery({ query: CURRENT_USER });

      // Precedence: User timezone > Org timezone > UTC
      return currentUser?.timezone || currentUser?.roles?.[0]?.org?.zone || 'UTC';
    } catch (error) {
      console.warn('Failed to read user timezone from Apollo cache', error);
      return 'UTC';
    }
  };

  const timezone = getTimezoneFromCache();

  /**
   * Convert an ISO timestamp string to the effective timezone
   * @param {string} isoString - ISO 8601 timestamp string (e.g., '2025-11-12T15:30:45Z')
   * @returns {string} - ISO formatted string in effective timezone (e.g., '2025-11-12T10:30:45')
   */
  const convertToUserTimezone = (isoString) => {
    const effectiveTimezone = getTimezoneFromCache();

    // Validate input
    if (!isoString || !moment(isoString).isValid()) {
      throw new Error('Invalid ISO string provided');
    }

    // Convert using moment-timezone 
    return moment(isoString)
      .tz(effectiveTimezone)
      .format('YYYY-MM-DDTHH:mm:ss');
  };

  return {
    timezone,
    convertToUserTimezone,
  };
};
